// Story 96 — the job's MOS queue priority (`jobPriority`).
//
// RN1/RN2/V1/V2/V3 (D20): an OPTIONAL integer greater than or equal to 1, with NO upper bound.
// Absence means "not prioritised" -- it is not a zero and not a "worse" value -- so an empty field is
// valid and travels as an absent value, never as 0.
//
// Deliberately NOT named `priority`: that name belongs to the Part's own 1..10 priority, which lives
// in the sibling "Part Settings" tab of the same dialog and is descending legacy (RN38/D24, risk R4).
//
// P5 — the server is the guarantee; this is UX prevention only. Sun validates the same rules on save.

/** Value the field carries when the job has no queue priority. */
export const EMPTY_JOB_PRIORITY = ''

/**
 * Whether what the user typed is an acceptable queue priority.
 * Accepts an empty value (RN3: the priority can be cleared) and any integer >= 1.
 * Rejects 0, negatives, decimals (in either notation) and any non-numeric text (E1).
 *
 * @param {string|number|null|undefined} value Raw field value
 * @returns {boolean}
 */
export const isJobPriorityValid = (value) => {
  const raw = String(value ?? '').trim()
  if (raw === EMPTY_JOB_PRIORITY) {
    return true
  }
  // Digits only: rules out '-3', '2.5', '2,5', '1e3' and 'abc' in one go, without Number()'s
  // permissiveness (Number('2.5') is a fine number, and Number('') is 0).
  if (!/^\d+$/.test(raw)) {
    return false
  }
  return Number(raw) >= 1
}

/**
 * The value to send to the server for a queue priority field, once valid: the integer, or an absent
 * value when the field was cleared (RN3/V3 -- never 0).
 *
 * @param {string|number|null|undefined} value Raw field value
 * @returns {number|null}
 */
export const toJobPriorityPayload = (value) => {
  const raw = String(value ?? '').trim()
  return raw === EMPTY_JOB_PRIORITY ? null : Number(raw)
}

/**
 * The value to show in the field for a job's stored queue priority: the number, or the empty value
 * when the job has none (RN36 -- the field comes up empty, never with a 0).
 *
 * @param {number|null|undefined} jobPriority Stored queue priority
 * @returns {string}
 */
export const toJobPriorityFieldValue = (jobPriority) =>
  jobPriority === null || jobPriority === undefined ? EMPTY_JOB_PRIORITY : String(jobPriority)
